import { useState, useCallback, useRef, useEffect } from 'react'
import Header from './components/Header/Header'
import Footer from './components/Footer/Footer'
import TurtleEditor from './components/Editor/TurtleEditor'
import OntologyCanvas from './components/Canvas/OntologyCanvas'
import { useOntology } from './hooks/useOntology'

const DEFAULT_TURTLE = `@prefix rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#> .
@prefix rdfs: <http://www.w3.org/2000/01/rdf-schema#> .
@prefix owl: <http://www.w3.org/2002/07/owl#> .
@prefix xsd: <http://www.w3.org/2001/XMLSchema#> .
@prefix ex: <http://example.org/ontology#> .

# Ontology Declaration
ex:MyOntology rdf:type owl:Ontology ;
    rdfs:label "Example Ontology"@en ;
    rdfs:comment "A sample ontology created with onto-maker"@en .

# Classes
ex:Person rdf:type owl:Class ;
    rdfs:label "Person"@en ;
    rdfs:comment "A human being"@en .

ex:Organization rdf:type owl:Class ;
    rdfs:label "Organization"@en ;
    rdfs:comment "A structured group of people"@en .

ex:Project rdf:type owl:Class ;
    rdfs:label "Project"@en ;
    rdfs:comment "A planned undertaking"@en .

# Properties
ex:worksFor rdf:type owl:ObjectProperty ;
    rdfs:label "works for"@en ;
    rdfs:domain ex:Person ;
    rdfs:range ex:Organization .

ex:manages rdf:type owl:ObjectProperty ;
    rdfs:label "manages"@en ;
    rdfs:domain ex:Person ;
    rdfs:range ex:Project .

ex:name rdf:type owl:DatatypeProperty ;
    rdfs:label "name"@en ;
    rdfs:domain ex:Person ;
    rdfs:range xsd:string .

ex:email rdf:type owl:DatatypeProperty ;
    rdfs:label "email"@en ;
    rdfs:domain ex:Person ;
    rdfs:range xsd:string .
`;

function App() {
    const [leftPanelWidth, setLeftPanelWidth] = useState(45); // percentage
    const resizerRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);

    const {
        turtleText,
        setTurtleText,
        validationErrors,
        ontologyStats,
        graph
    } = useOntology(DEFAULT_TURTLE);

    // Resizer logic
    const handleMouseDown = useCallback(() => {
        isDragging.current = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, []);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging.current) return;
            const newWidth = (e.clientX / window.innerWidth) * 100;
            if (newWidth >= 20 && newWidth <= 70) {
                setLeftPanelWidth(newWidth);
            }
        };

        const handleMouseUp = () => {
            isDragging.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    return (
        <div className="app">
            <Header />

            <div className="banner">
                <div className="banner__content">
                    <span>🔧 Double-click nodes to edit • Click canvas to create new classes • Drag to connect properties • Use Ctrl+S to save</span>
                    <span>📦 Export to Turtle, JSON-LD, RDF/XML • Real-time validation • PCB-style routing</span>
                    <span>🔧 Double-click nodes to edit • Click canvas to create new classes • Drag to connect properties • Use Ctrl+S to save</span>
                    <span>📦 Export to Turtle, JSON-LD, RDF/XML • Real-time validation • PCB-style routing</span>
                </div>
            </div>

            <main className="main">
                {/* Editor Panel */}
                <section
                    className="panel panel--editor"
                    style={{ width: `${leftPanelWidth}%` }}
                >
                    <div className="panel__header">
                        <div className="panel__header-title">
                            <span className="panel__header-icon">◆</span>
                            <span>Turtle Editor</span>
                        </div>
                        <div className={`status ${validationErrors.length === 0 ? 'status--valid' : 'status--error'}`}>
                            <span className="status__dot"></span>
                            <span>{validationErrors.length === 0 ? 'Valid RDF' : `${validationErrors.length} errors`}</span>
                        </div>
                    </div>
                    <div className="panel__content">
                        <TurtleEditor
                            value={turtleText}
                            onChange={setTurtleText}
                            errors={validationErrors}
                        />
                    </div>
                </section>

                {/* Resizer */}
                <div
                    ref={resizerRef}
                    className="resizer"
                    onMouseDown={handleMouseDown}
                />

                {/* Canvas Panel */}
                <section
                    className="panel panel--canvas"
                    style={{ width: `${100 - leftPanelWidth}%` }}
                >
                    <div className="panel__header">
                        <div className="panel__header-title">
                            <span className="panel__header-icon">◈</span>
                            <span>Ontology Visualization</span>
                        </div>
                        <div className="viz-tabs">
                            <button className="viz-tab viz-tab--active">Canvas</button>
                            <button className="viz-tab">Network</button>
                            <button className="viz-tab">Hierarchy</button>
                        </div>
                    </div>
                    <div className="panel__content">
                        <OntologyCanvas graph={graph} />
                    </div>
                </section>
            </main>

            <Footer stats={ontologyStats} />
        </div>
    );
}

export default App;
